import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mergeGuestMobileTabs, type GuestMobileTabsStored } from "@heylife/core";
import { api } from "../api";
import { initialFeatureSave, noteFeatureEdit, noteFeatureSaveFinish, noteFeatureSaveStart } from "../featureSave";
import { useT } from "../i18n";
import { Msg, Toggle, useFlash } from "../ui";

type GuestTabsPayload = {
  stored: GuestMobileTabsStored;
  effective: Record<string, boolean>;
  catalog: string[];
};

/** Superadmin: which bottom-nav tabs logged-out guests and church-less accounts see. */
export function GuestTabs() {
  const { t } = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin", "platform", "guest-tabs"], queryFn: () => api<GuestTabsPayload>("/admin/platform/guest-tabs") });
  const [draft, setDraft] = useState<GuestMobileTabsStored | null>(null);
  const [flash, run, busy] = useFlash();
  const queueRef = useRef(initialFeatureSave());

  const stored = mergeGuestMobileTabs(q.data?.stored ?? {}, draft ?? {});
  const effective: Record<string, boolean> = { ...(q.data?.effective ?? {}), ...(draft?.tabs ?? {}) };

  const setTab = (k: string, on: boolean) => setDraft((d) => ({ tabs: { ...d?.tabs, [k]: on } }));

  const dirty = draft !== null;
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const timerRef = useRef<number | null>(null);
  const tRef = useRef(t);
  tRef.current = t;

  const saveMut = useMutation({
    mutationFn: (payload: GuestMobileTabsStored) => api("/admin/platform/guest-tabs", { method: "PATCH", json: payload }),
  });

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const armTimer = () => {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void flush();
    }, 500);
  };

  const flush = async () => {
    const patch = draftRef.current;
    if (!patch || queueRef.current.inFlightGen !== null) return;
    const savedGen = queueRef.current.gen;
    queueRef.current = noteFeatureSaveStart(queueRef.current);
    const ok = await run(() => saveMut.mutateAsync(patch), tRef.current("admin.features.guestTabs.saved"));
    const done = noteFeatureSaveFinish(queueRef.current, savedGen);
    queueRef.current = done.queue;
    if (!ok) return;
    if (done.clearDraft) {
      setDraft(null);
      await qc.invalidateQueries({ queryKey: ["admin", "platform", "guest-tabs"] });
      return;
    }
    if (done.armTimer) armTimer();
  };

  const save = () => {
    if (!draftRef.current) return;
    clearTimer();
    void flush();
  };

  useEffect(() => {
    if (!draft) return;
    const next = noteFeatureEdit(queueRef.current);
    queueRef.current = next.queue;
    if (next.armTimer) armTimer();
    return () => clearTimer();
  }, [draft]);

  if (q.isLoading) return <p className="muted">{t("admin.common.loading")}</p>;
  if (q.isError || !q.data) return <p className="error">{t("admin.common.error")}</p>;

  return (
    <div className="card feature-matrix">
      <h3>{t("admin.features.guestTabs.title")}</h3>
      <p className="muted">{t("admin.features.guestTabs.hint")}</p>
      <section className="panel matrix-section">
        <h4>{t("admin.features.mobileTabsSection")}</h4>
        <div className="matrix-rows">
          {q.data.catalog.map((k) => {
            const flags: Record<string, boolean | undefined> = stored.tabs ?? {};
            const storedValue = flags[k];
            const on = effective[k] ?? true;
            const changed = storedValue !== undefined && storedValue !== on;
            return (
              <div key={k} className={`matrix-row ${changed ? "changed" : ""}`}>
                <span>{t(`tabs.${k}`)}</span>
                <Toggle label="" checked={on} onChange={(next) => setTab(k, next)} />
              </div>
            );
          })}
        </div>
      </section>
      <div className="actions">
        <button className="btn small" disabled={!dirty || busy} onClick={save}>
          {t("admin.common.save")}
        </button>
        <button type="button" className="btn small ghost" disabled={!dirty} onClick={() => setDraft(null)}>
          {t("admin.common.cancel")}
        </button>
      </div>
      <Msg msg={flash} />
    </div>
  );
}
